import {useRef, type ReactNode} from 'react'
import type {ClientDiffLine} from '../types'
import type {PrunedIconButtonProps} from './ExpandableHunkHeaderDiffLine'
import {isEmptyDiffLine} from '../helpers/line-helpers'
import {Cell, HunkKebabIcon, useGridCellButtonProps} from './DiffLineTableCellParts'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'

/**
 * Renders a hunk header cell
 */
export function HunkCell({
  diffLine,
  renderHunkButton,
}: {
  diffLine: ClientDiffLine
  renderHunkButton?: (additionalProps: PrunedIconButtonProps) => ReactNode | null
}) {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const [cellProps, buttonProps] = useGridCellButtonProps(cellRef)

  if (isEmptyDiffLine(diffLine)) return null

  const key = `HunkCell-left:${diffLine.left}-right:${diffLine.right}`

  return (
    <Cell
      key={key}
      ref={cellRef}
      colSpan={4}
      columnIndex={0}
      diffLine={diffLine}
      style={{backgroundColor: 'var(--bgColor-accent-muted, var(--color-accent-subtle))', flexGrow: 1}}
      {...cellProps}
      className="diff-hunk-cell"
    >
      <div className="d-flex flex-row">
        {renderHunkButton?.(buttonProps) ?? <HunkKebabIcon />}
        <code className="diff-text-cell hunk">
          {/* Explicitly mark html as safe because it is server-sanitized */}
          <VerifiedHTMLDiv className="diff-text-inner color-fg-muted" html={diffLine.html as SafeHTMLString} />
        </code>
      </div>
    </Cell>
  )
}

try{ HunkCell.displayName ||= 'HunkCell' } catch {}